/**
 * Card interaction logic: keyboard handling and aria/role/tabIndex derivation.
 *
 * @internal Used by Card atom only.
 */

import type React from 'react';

export interface CardInteractionState {
  role: 'button' | undefined;
  tabIndex: 0 | undefined;
  ariaDisabled: true | undefined;
  isClickable: boolean;
  isInteractive: boolean;
}

export function getCardInteractionState(
  onClick: ((e: React.MouseEvent<HTMLDivElement>) => void) | undefined,
  interactive: boolean | undefined,
  disabled: boolean
): CardInteractionState {
  const hasClick = Boolean(onClick);
  const isInteractive = interactive ?? hasClick;
  const isClickable = hasClick && !disabled;
  const isButtonLike = isClickable && isInteractive;

  return {
    role: isButtonLike ? 'button' : undefined,
    tabIndex: isButtonLike ? 0 : undefined,
    ariaDisabled: disabled ? true : undefined,
    isClickable,
    isInteractive,
  };
}

export function createCardKeyDownHandler(
  onClick: ((e: React.MouseEvent<HTMLDivElement>) => void) | undefined,
  isClickable: boolean,
  isInteractive: boolean,
  onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>
): React.KeyboardEventHandler<HTMLDivElement> {
  return (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (onKeyDown) {
      onKeyDown(e);
      if (e.defaultPrevented) return;
    }
    if (!isClickable || !isInteractive) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      const synthetic = { preventDefault: () => {} } as React.MouseEvent<HTMLDivElement>;
      onClick?.(synthetic);
    }
  };
}
